import { Many, One, relations } from "drizzle-orm"; // Import One and Many
import { boolean, integer, jsonb, numeric, pgEnum, pgTable, primaryKey, text, timestamp } from "drizzle-orm/pg-core";

import { orderItems } from "./orders"; // Import orderItems
import { user } from "./auth"; // Assuming user schema is in auth.ts

// Enum for activation code status
export const activationCodeStatusEnum = pgEnum("activation_code_status", [
  "available", // Newly created, not yet assigned or activated
  "assigned",  // Assigned to an email/user, but not yet activated
  "active",    // Activated by a user and currently in use
  "expired",   // Past its expiry date
  "revoked",   // Manually revoked by an admin
]);

// Enum for activation code source
export const activationCodeSourceEnum = pgEnum("activation_code_source", [
  "manual",   // Created manually by admin
  "purchase", // Created through direct purchase
  "api",      // Created through API
]);

// Products Table
export const products = pgTable("products", {
  id: text("id").primaryKey().$defaultFn(() => crypto.randomUUID()), // Using crypto.randomUUID() for default
  name: text("name").notNull(), // e.g., "Personal Plan", "Team Plan"
  description: text("description"),
  monthlyPrice: numeric("monthly_price"), // 添加月度价格
  yearlyPrice: numeric("yearly_price"), // 添加年度价格
  monthlyPurchasable: boolean("monthly_purchasable").default(false), // 月度价格是否开放购买
  yearlyPurchasable: boolean("yearly_purchasable").default(false), // 年度价格是否开放购买，控制前端显示和API行为
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull().$onUpdate(() => new Date()),
  customFeatures: jsonb("custom_features"), // Stores custom key-value pairs for product-specific features
  maxBindingsPerCode: integer("max_bindings_per_code").default(1).notNull(), // Max domains/IPs an associated code can be bound to
  position: integer("position"), // For product sorting order, nulls last by default or handle explicitly
});

export const productsRelations = relations(products, ({ many: Many }) => ({
  activationCodes: Many(activationCodes),
  productFeatures: Many(productFeatures),
}));

// Features Table
// Represents individual features that can be enabled for a product
export const features = pgTable("features", {
  id: text("id").primaryKey().$defaultFn(() => crypto.randomUUID()),
  key: text("key").notNull().unique(), // e.g., "advanced_reporting", "max_users_5"
  name: text("name").notNull(), // e.g., "Advanced Reporting", "Up to 5 Users"
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull().$onUpdate(() => new Date()),
});

export const featuresRelations = relations(features, ({ many: Many }) => ({
  productFeatures: Many(productFeatures),
}));

// ProductFeatures Join Table (Many-to-Many between Products and Features)
export const productFeatures = pgTable("product_features", {
  productId: text("product_id").notNull().references(() => products.id, { onDelete: "cascade" }),
  featureId: text("feature_id").notNull().references(() => features.id, { onDelete: "cascade" }),
  // metadata: jsonb("metadata"), // Optional: for feature-specific configuration within a product context
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.productId, table.featureId] }),
}));

export const productFeaturesRelations = relations(productFeatures, ({ one: One }) => ({
  product: One(products, {
    fields: [productFeatures.productId],
    references: [products.id],
  }),
  feature: One(features, {
    fields: [productFeatures.featureId],
    references: [features.id],
  })
}));

// Enum for binding type
export const bindingTypeEnum = pgEnum("binding_type", [
  "domain",
  "ip",
]);

// Activation Code Bindings Table
export const activationCodeBindings = pgTable("activation_code_bindings", {
  id: text("id").primaryKey().$defaultFn(() => crypto.randomUUID()),
  activationCodeId: text("activation_code_id").notNull().references(() => activationCodes.id, { onDelete: "cascade" }),
  bindingType: bindingTypeEnum("binding_type").notNull(),
  bindingValue: text("binding_value").notNull(), // e.g., "example.com" or "192.168.1.1"
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull().$onUpdate(() => new Date()),
});

export const activationCodeBindingsRelations = relations(activationCodeBindings, ({ one: One }) => ({
  activationCode: One(activationCodes, {
    fields: [activationCodeBindings.activationCodeId],
    references: [activationCodes.id],
  }),
}));

// Activation Codes Table
export const activationCodes = pgTable("activation_codes", {
  id: text("id").primaryKey().$defaultFn(() => crypto.randomUUID()),
  code: text("code").notNull().unique(), // The actual activation code string
  productId: text("product_id").notNull().references(() => products.id, { onDelete: "restrict" }), // Don't delete product if codes exist
  orderItemId: text("order_item_id").references(() => orderItems.id, { onDelete: "set null" }), // Null if not from an order or if orderItem deleted
  userId: text("user_id").references(() => user.id, { onDelete: "set null" }), // User who activated/owns this code
  assignedEmail: text("assigned_email"), // Email this code is intended for or was used by for activation if no user account initially
  status: activationCodeStatusEnum("status").default("available").notNull(),
  source: activationCodeSourceEnum("source").default("manual").notNull(), // Source of the activation code
  expiresAt: timestamp("expires_at").notNull(), // "激活码有有效期"
  activatedAt: timestamp("activated_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull().$onUpdate(() => new Date()),
});

export const activationCodesRelations = relations(activationCodes, ({ one: One, many: Many }) => ({
  product: One(products, {
    fields: [activationCodes.productId],
    references: [products.id],
  }),
  user: One(user, {
    fields: [activationCodes.userId],
    references: [user.id],
  }),
  orderItem: One(orderItems, {
    fields: [activationCodes.orderItemId],
    references: [orderItems.id],
  }),
  activationCodeBindings: Many(activationCodeBindings), // Add relation to bindings
}));